/**
 * Resolves a flow-analysis `paths[].route` (task NAME strings, with bracketed
 * "[ A → B ∥ C ]" segments for parallel/inclusive branches — see backend
 * FlowAnalysisService.routeOf) into the BPMN element IDs the generated diagram
 * actually uses, so a route can be walked on the live bpmn-js canvas.
 *
 * Element IDs follow the fixed naming convention BpmnXmlBuilderService emits:
 * `Activity_<task_id>` for a normal task, `SubProcess_<child_process_id>` for
 * an embedded sub-process slot (flow-analysis rows carry the sub-process's own
 * NEGATIVE synthetic task_id, but child_process_id is the real positive id the
 * diagram element uses).
 */
export function buildTaskElementLookup(tasks) {
  const lookup = new Map();
  for (const t of tasks || []) {
    if (!t || !t.task_name) continue;
    const elementId = t.is_subprocess
      ? `SubProcess_${t.child_process_id}`
      : `Activity_${t.task_id}`;
    // Trim: some stored task names carry stray leading/trailing whitespace (seen live —
    // task 4532 is stored as "\t Prepare Admission plan") that the rendered diagram's
    // SVG text collapses away visually but a raw string match would not. Route names
    // below are trimmed the same way, so this keeps both sides matching regardless.
    lookup.set(t.task_name.trim(), elementId);
  }
  return lookup;
}

/**
 * Turns a route (array of task-name strings, some possibly bracketed parallel
 * groups) into an ordered list of animation "steps". Each step is either a
 * single element id (sequential task) or `{ concurrent: [ids] }` (a
 * parallel/inclusive branch group, highlighted together since they run at the
 * same time). Names that don't resolve to a known element are skipped and
 * reported in `unresolved` rather than breaking the walk.
 */
export function resolveRouteToSteps(route, lookup) {
  const steps = [];
  const unresolved = [];
  for (const raw of route || []) {
    const name = String(raw || "").trim();
    if (!name) continue;
    if (name.startsWith("[") && name.endsWith("]")) {
      const inner = name.slice(1, -1).trim();
      const branchNames = inner
        .split(" ∥ ")
        .flatMap((branch) => branch.split(" → ").map((s) => s.trim()))
        .filter(Boolean);
      const ids = [];
      for (const n of branchNames) {
        const id = lookup.get(n);
        if (id) ids.push(id);
        else unresolved.push(n);
      }
      if (ids.length) steps.push({ concurrent: ids });
      continue;
    }
    const id = lookup.get(name);
    if (id) steps.push(id);
    else unresolved.push(name);
  }
  return { steps, unresolved };
}
